/**
 * UserFeeOverridesPanel — Admin: Per-User Fee Overrides
 *
 * Zeigt pro User die 5 Fee-Bereiche mit optionaler Sonderkondition.
 * - Neuer Override: Wert + Zeitraum (1-24 Monate ODER unbegrenzt) → POST
 * - Aktiver Override anzeigen: Wert · verbleibende Tage · Löschen-Button
 * - Kein Override → zeigt effektive globale Fee (read-only)
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Loader2, Save, Trash2, AlertCircle, Percent, Euro } from "lucide-react";

const AREA_META = {
  STX_SHOP:           { label: "STX Shop",           type: "pct"  },
  STX_RESCUE:         { label: "STX Rescue",         type: "pct"  },
  SUBSCRIPTION:       { label: "Creator-Abo",        type: "pct"  },
  PAID_MEDIA:         { label: "Paid-Media",         type: "pct"  },
  MP_BUYER_PROTECTION:{ label: "MP Käuferschutz",    type: "flat" },
};
const ORDERED = ["STX_SHOP", "STX_RESCUE", "SUBSCRIPTION", "PAID_MEDIA", "MP_BUYER_PROTECTION"];

function formatFee(area, raw) {
  const t = AREA_META[area].type;
  if (raw === undefined || raw === null) return "—";
  return t === "pct" ? `${(Number(raw) * 100).toFixed(2)} %` : `${Number(raw).toFixed(2)} €`;
}

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export default function UserFeeOverridesPanel({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drafts, setDrafts] = useState({});    // { area: { value: str, duration: "6" | "UNLIMITED" } }
  const [savingArea, setSavingArea] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: res } = await api.get(`/admin/users/${userId}/effective-fees`);
      setData(res);
      // Prefill drafts from active overrides
      const nextDrafts = {};
      (res.active_overrides || []).forEach((ov) => {
        const t = AREA_META[ov.area]?.type;
        nextDrafts[ov.area] = {
          value: t === "pct" ? (ov.value * 100).toFixed(2) : Number(ov.value).toFixed(2),
          duration: ov.duration_months ? String(ov.duration_months) : "UNLIMITED",
          existing: ov,
        };
      });
      setDrafts(nextDrafts);
    } catch (e) {
      setError(e?.response?.data?.detail || "Konnte User-Fees nicht laden.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  function setDraft(area, patch) {
    setDrafts((d) => ({ ...d, [area]: { ...(d[area] || { value: "", duration: "UNLIMITED" }), ...patch } }));
  }

  async function save(area) {
    setError("");
    setSavingArea(area);
    try {
      const t = AREA_META[area].type;
      const raw = Number(drafts[area]?.value);
      if (Number.isNaN(raw)) throw new Error("Wert ist keine Zahl");
      let val;
      if (t === "pct") {
        if (raw < 0 || raw > 100) throw new Error("Prozent 0..100");
        val = Math.round((raw / 100) * 10000) / 10000;
      } else {
        if (raw < 0 || raw > 999) throw new Error("EUR-Wert 0..999");
        val = Math.round(raw * 100) / 100;
      }
      const dur = drafts[area]?.duration;
      const duration_months = dur === "UNLIMITED" ? null : Number(dur);
      await api.post(`/admin/users/${userId}/fee-overrides`, {
        area, value: val, duration_months,
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSavingArea(null);
    }
  }

  async function remove(overrideId) {
    setError("");
    setDeletingId(overrideId);
    try {
      await api.delete(`/admin/users/${userId}/fee-overrides/${overrideId}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) {
    return (
      <div data-testid="user-fee-overrides-loading" className="flex items-center gap-2 text-xs text-[#666]">
        <Loader2 size={12} className="animate-spin" /> Lade Fee-Overrides…
      </div>
    );
  }

  const globals = data?.globals || {};
  const effective = data?.effective || {};

  return (
    <div data-testid="user-fee-overrides-panel" className="space-y-3">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-1">Servicegebühren · Sonderkonditionen</p>
        <p className="text-[10px] text-[#999]">Werte werden pro Bereich überschrieben. Läuft der Zeitraum ab, greift automatisch der globale Default.</p>
      </div>

      {error && (
        <div data-testid="user-fee-error" className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 text-[11px] text-red-700">
          <AlertCircle size={12} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}

      <div className="space-y-2">
        {ORDERED.map((area) => {
          const meta = AREA_META[area];
          const draft = drafts[area] || { value: "", duration: "UNLIMITED" };
          const existing = draft.existing || null;
          const isPct = meta.type === "pct";
          const globalV = globals[area];
          const effV = effective[area];
          const hasOverride = effV !== globalV;
          return (
            <div key={area} data-testid={`user-fee-row-${area}`} className="p-3 border border-[#E5E5E5] bg-white">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-[#0A0A0A]">{meta.label}</p>
                <p className="text-[10px] text-[#666]">
                  Global: <span className="tabular-nums">{formatFee(area, globalV)}</span>
                  {hasOverride && (
                    <span className="ml-2 text-amber-700">
                      · aktiv: <span data-testid={`user-fee-effective-${area}`} className="font-semibold tabular-nums">{formatFee(area, effV)}</span>
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[100px]">
                  <input
                    data-testid={`user-fee-input-${area}`}
                    type="number"
                    step="0.01"
                    min="0"
                    max={isPct ? "100" : "999"}
                    value={draft.value}
                    onChange={(e) => setDraft(area, { value: e.target.value })}
                    placeholder={isPct ? `${(globalV * 100).toFixed(1)}` : `${Number(globalV).toFixed(2)}`}
                    className="w-full h-9 px-3 pr-8 border border-[#E5E5E5] text-xs focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999]">
                    {isPct ? <Percent size={10} /> : <Euro size={10} />}
                  </span>
                </div>
                <select
                  data-testid={`user-fee-duration-${area}`}
                  value={draft.duration}
                  onChange={(e) => setDraft(area, { duration: e.target.value })}
                  className="h-9 px-3 border border-[#E5E5E5] text-xs bg-white"
                >
                  <option value="UNLIMITED">unbegrenzt</option>
                  {[1, 2, 3, 6, 9, 12, 18, 24].map((m) => (
                    <option key={m} value={String(m)}>{m} {m === 1 ? "Monat" : "Monate"}</option>
                  ))}
                </select>
                <button
                  data-testid={`user-fee-save-${area}`}
                  onClick={() => save(area)}
                  disabled={savingArea === area || !draft.value}
                  className="h-9 px-3 bg-[#0A0A0A] text-white text-xs font-medium flex items-center gap-1 disabled:opacity-40"
                >
                  {savingArea === area ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                  Setzen
                </button>
                {existing && (
                  <button
                    data-testid={`user-fee-delete-${area}`}
                    onClick={() => remove(existing.override_id)}
                    disabled={deletingId === existing.override_id}
                    className="h-9 px-3 border border-red-200 text-red-600 text-xs font-medium flex items-center gap-1 hover:bg-red-50 disabled:opacity-40"
                  >
                    <Trash2 size={10} /> Entfernen
                  </button>
                )}
              </div>
              {existing && (
                <p data-testid={`user-fee-info-${area}`} className="mt-1.5 text-[10px] text-[#666]">
                  Aktueller Override: {formatFee(area, existing.value)} ·{" "}
                  {existing.expires_at
                    ? `läuft in ${daysUntil(existing.expires_at)} Tagen ab (${new Date(existing.expires_at).toLocaleDateString("de-DE")})`
                    : "unbegrenzt"}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
